import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Divider as _Divider, Paper, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import { ClassAttributes, createElement, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProjectChangeType } from '../../../service/persistent/ProjectChangeWebApi';
import { ProjectType } from '../../../service/persistent/ProjectWebApi';
import CreativeBriefDetailDiff from '../../component/compound/CreativeBriefDetailDiff';
import DummyCreativeBriefDetail from '../../component/compound/DummyCreativeBriefDetail';
import DummyProjectTypeSummary from '../../component/compound/DummyCreativeBriefDetail/DummyProjectTypeSummary';
import ProjectBasicSettingDetailDiff from '../../component/compound/ProjectBasicSettingDetailDiff';
import ProjectTypeSummary from '../../component/compound/ProjectTypeSummary';
import { SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';

type Props = ClassAttributes<HTMLElement> & {
  project: ProjectType | null;
  projectChange: ProjectChangeType;
  latestBriefFiles: Map<string, FileReference[]>;
  beforeBriefFiles: Map<string, FileReference[]>;
  assetFiles: ReadonlyArray<FileReference>;
  briefFiles: Map<string, FileReference[]>;
  loading: boolean;
  className?: string;
};

const CreativeBrief = ({
  project,
  projectChange,
  latestBriefFiles,
  beforeBriefFiles,
  assetFiles,
  briefFiles,
  loading,
  className,
}: Props) => {
  let projectTypeSummary: ReactElement<any>;
  let brief: ReactElement<any>;
  let basicSetting: ReactElement<any> | null = null;

  if (
    loading ||
    !project ||
    !projectChange ||
    !projectChange.latestVersion.creativeBriefValue ||
    !projectChange.beforeVersion.creativeBriefValue
  ) {
    projectTypeSummary = <DummyProjectTypeSummary />;
    brief = <DummyCreativeBriefDetail />;
  } else {
    projectTypeSummary = (
      <ProjectTypeSummary
        projectId={project.id}
        hasProjectChange={true}
        projectType={project.projectType}
        noShowDiff={true}
      />
    );
    brief = (
      <CreativeBriefDetailDiff
        // ここをProjectChangeのlatestVerstion: Project, とbeforeVersion: Projectを使うようにする
        definition={project.creativeBriefDefinition}
        value={project.creativeBriefValue!}
        latestValue={projectChange.latestVersion.creativeBriefValue.values}
        beforeValue={projectChange.beforeVersion.creativeBriefValue.values}
        files={briefFiles}
        latestFiles={latestBriefFiles}
        beforeFiles={beforeBriefFiles}
      />
    );
    if (project != null && projectChange.latestVersion.assetCategoryValue != null) {
      basicSetting = (
        // asset表示？ ここも対応する必要あるかも
        <ProjectBasicSettingDetailDiff
          assetFiles={assetFiles}
          latestAssetValue={projectChange.latestVersion.assetCategoryValue}
          beforeAssetValue={projectChange.beforeVersion.assetCategoryValue}
          assetCategorySections={
            project.assetCategoryDefinition != null ? project.assetCategoryDefinition.sections : null
          }
        />
      );
    }
  }

  return (
    <Root className={className}>
      <Heading variant="h5">
        <FormattedMessage id="project_detail_page.creative_brief.heading" />
        {projectChange ? (
          <UpdateTitle>
            <FormattedMessage id="project_detail_page.creative_brief.updated_at" />:{' '}
            {DateTime.fromISO(projectChange.updatedAt).toFormat('yyyy-MM-dd HH:mm:ss')}
          </UpdateTitle>
        ) : null}
      </Heading>
      <Content>
        {projectTypeSummary}
        <Divider />
        {basicSetting}
        <Divider />
        {brief}
      </Content>
    </Root>
  );
};

const Root = styled('div')({
  ...gridLayoutContainer({
    columns: ['100%'],
    rows: ['auto', 'auto'],
    areas: [['heading'], ['content']],
  }),
});

const Heading = styled(Typography)({
  ...gridLayoutItem('heading'),
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: SPACING_UNIT * 3,
});

const UpdateTitle = styled('div')({
  fontSize: '18px',
});
const Content = styled(Paper)({
  ...gridLayoutItem('content'),
  padding: `${SPACING_UNIT * 3}px ${SPACING_UNIT * 2}px`,
});

const Divider = styled(_Divider)({
  margin: `${SPACING_UNIT * 3}px 0`,
});

export default CreativeBrief;
